import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { User } from "firebase/auth";
import { UserProfile, UserAccountType, OFFICIAL_ADMIN_EMAILS } from "../types/userProfile";
import {
  loadUserProfile,
  saveUserProfile,
  clearUserProfileOnLogout,
  isProfileIncomplete,
  DEFAULT_USER_PROFILE,
} from "../utils/userProfileStorage";
import {
  db,
  isFirebaseConfigured,
  onAuthStateChange,
  loadUserProfileFirestore,
  saveUserProfileFirestore,
  loginWithGoogle,
  logoutUser,
} from "../utils/firebase";

export interface AuthContextType {
  user: User | null;
  userProfile: UserProfile;
  isAuthReady: boolean;
  isLoggedIn: boolean;
  showGoogleAuthModal: boolean;
  setShowGoogleAuthModal: (show: boolean) => void;
  authModalRole: UserAccountType;
  openAuthModalWithRole: (role?: UserAccountType) => void;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
  updateUserProfile: (updated: UserProfile) => void;
  handleGoogleLogin: () => Promise<void>;
  handleGoogleLogout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode; onToast?: (msg: string, type?: "success" | "error") => void }> = ({
  children,
  onToast,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile>(() => loadUserProfile());
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [showGoogleAuthModal, setShowGoogleAuthModal] = useState(false);
  const [authModalRole, setAuthModalRole] = useState<UserAccountType>("atleta");

  const isLoggedIn = Boolean(user && user.uid);

  useEffect(() => {
    let unsubAuth = () => {};

    if (isFirebaseConfigured()) {
      unsubAuth = onAuthStateChange(async (currentUser) => {
        setUser(currentUser);
        setIsAuthReady(true);

        if (currentUser) {
          // Busca perfil local ESTRITAMENTE pelo UID ou Email do usuário autenticado atual
          let localProfile = loadUserProfile(currentUser.uid);
          if ((!localProfile || localProfile.id !== currentUser.uid) && currentUser.email) {
            const emailProfile = loadUserProfile(currentUser.email);
            if (emailProfile && (emailProfile.id === currentUser.uid || emailProfile.email?.toLowerCase() === currentUser.email.toLowerCase())) {
              localProfile = emailProfile;
            }
          }

          // Validação estrita de posse: localProfile só pode ser considerado se pertencer comprovadamente a este usuário
          const isLocalProfileValidForUser = Boolean(
            localProfile &&
            localProfile.id !== "guest_visitor" &&
            (localProfile.id === currentUser.uid || (currentUser.email && localProfile.email?.toLowerCase() === currentUser.email.toLowerCase()))
          );

          const safeLocalProfile = isLocalProfileValidForUser ? localProfile : null;

          let cloudProfile = await loadUserProfileFirestore(currentUser.uid);
          if (!cloudProfile && currentUser.email) {
            cloudProfile = await loadUserProfileFirestore(currentUser.email);
          }

          let baseProfile: UserProfile;
          if (cloudProfile && cloudProfile.id !== "guest_visitor") {
            // Prioridade autoritativa para o perfil do Firestore (users/{currentUser.uid})
            baseProfile = {
              ...DEFAULT_USER_PROFILE,
              ...(safeLocalProfile ? safeLocalProfile : {}),
              ...cloudProfile,
            };
          } else if (safeLocalProfile) {
            baseProfile = {
              ...DEFAULT_USER_PROFILE,
              ...safeLocalProfile,
            };
          } else {
            // Novo usuário sem perfil: ESTRITAMENTE atleta por default determinístico
            baseProfile = {
              ...DEFAULT_USER_PROFILE,
              id: currentUser.uid,
              accountType: "atleta",
              role: "atleta",
            };
          }

          const isSuperAdminEmail = currentUser.email && OFFICIAL_ADMIN_EMAILS.includes(currentUser.email.toLowerCase());

          // Resolução do accountType: nunca defaultar para dono_arena
          const resolvedAccountType: UserAccountType =
            baseProfile.accountType === "dono_arena" ? "dono_arena" : "atleta";

          const updated: UserProfile = {
            ...baseProfile,
            id: currentUser.uid,
            name:
              baseProfile.name &&
              !baseProfile.name.toLowerCase().includes("visitante") &&
              baseProfile.name !== "Usuário Google"
                ? baseProfile.name
                : currentUser.displayName || "Usuário Google",
            email: currentUser.email || baseProfile.email || "",
            avatarUrl: currentUser.photoURL || baseProfile.avatarUrl || "",
            accountType: resolvedAccountType,
            role: isSuperAdminEmail ? "admin" : (baseProfile.role || (resolvedAccountType === "dono_arena" ? "dono_arena" : "atleta")),
            isSuperAdmin: isSuperAdminEmail ? true : Boolean(baseProfile.isSuperAdmin),
          };

          saveUserProfile(updated);
          if (!cloudProfile) {
            saveUserProfileFirestore(updated).catch(() => {});
          }
          setUserProfile(updated);

          if (isProfileIncomplete(updated)) {
            onToast?.("⚠️ Complete seu perfil com Cidade, Estado e Telefone!", "error");
          }
        } else {
          clearUserProfileOnLogout();
          setUserProfile(DEFAULT_USER_PROFILE);
        }
      });
    } else {
      setIsAuthReady(true);
    }

    return () => {
      unsubAuth();
    };
  }, []);

  const openAuthModalWithRole = (role?: UserAccountType) => {
    if (role) {
      setAuthModalRole(role);
    } else {
      setAuthModalRole("atleta");
    }
    setShowGoogleAuthModal(true);
  };

  const updateUserProfile = (updated: UserProfile) => {
    setUserProfile(updated);
    saveUserProfile(updated);
  };

  const handleGoogleLogin = async () => {
    try {
      const loggedUser = await loginWithGoogle();
      if (loggedUser) {
        onToast?.(`Bem-vindo, ${loggedUser.displayName || "Jogador"}!`, "success");
      }
    } catch (error) {
      onToast?.("Falha ao entrar com Google.", "error");
    }
  };

  const handleGoogleLogout = async () => {
    if (window.confirm("Deseja realmente sair da sua conta?")) {
      try {
        await logoutUser();
        setUser(null);
        clearUserProfileOnLogout();
        setUserProfile(DEFAULT_USER_PROFILE);
        if (typeof window !== "undefined" && window.sessionStorage) {
          sessionStorage.clear();
        }
        onToast?.("Você saiu da conta com sucesso!", "success");
        // Redireciona para a Landing Page pública (/)
        if (typeof window !== "undefined") {
          if (window.location.pathname !== "/") {
            window.history.pushState({}, "", "/");
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        }
      } catch (error) {
        console.error("Erro no logout:", error);
        onToast?.("Erro ao sair da conta.", "error");
      }
    }
  };

  const value = useMemo(
    () => ({
      user,
      userProfile,
      isAuthReady,
      isLoggedIn,
      showGoogleAuthModal,
      setShowGoogleAuthModal,
      authModalRole,
      openAuthModalWithRole,
      setUserProfile,
      updateUserProfile,
      handleGoogleLogin,
      handleGoogleLogout,
    }),
    [user, userProfile, isAuthReady, isLoggedIn, showGoogleAuthModal, authModalRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};
